import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AdminFormPage,
  useAdminFormPersistence,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useV2GetBusinessUnitQuery } from "@generated";
import { BusinessUnitForm } from "../BusinessUnitForm/BusinessUnitForm";
import { useBusinessUnitMutations } from "../api";
import { emptyBusinessUnitForm } from "../types";
import type { BusinessUnitFormValues } from "../types";

const FORM_ID = "business-unit-form";

export const Edit = (): JSX.Element => {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { update } = useBusinessUnitMutations();
  const { data, isLoading } = useV2GetBusinessUnitQuery({ id: Number(id) });
  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<BusinessUnitFormValues>();
  const [submitting, setSubmitting] = useState(false);

  const loadedInitial = useMemo<BusinessUnitFormValues>(() => {
    const attrs = data?.data?.attributes;
    if (!attrs) return emptyBusinessUnitForm;
    return {
      name: attrs.name ?? "",
      externalName: attrs["external-name"] ?? "",
      streetAddress: attrs["street-address"] ?? "",
      street2: attrs.street2 ?? "",
      city: attrs.city ?? "",
      state: attrs.state ?? "",
      postalCode: attrs["postal-code"] ?? "",
      country: attrs.country ?? "",
    };
  }, [data]);

  if (isLoading) return <ScopeStackSpinner />;

  const initialValues = savedInitial ?? loadedInitial;

  const handleSubmit = async (values: BusinessUnitFormValues) => {
    setSubmitting(true);
    try {
      await update(id, values);
      markSaved(values, `'${values.name}' saved`);
    } catch (err) {
      console.error("Failed to update business unit", err);
      setToast({ variant: "danger", text: "Could not save business unit." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormPage
        title="Edit Business Unit"
        formId={FORM_ID}
        onCancel={() => navigate("../..", { relative: "path" })}
        isSubmitting={submitting}
      >
        <BusinessUnitForm
          key={formKey}
          id={FORM_ID}
          initialValues={initialValues}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default Edit;
